/**
 * Shared by the native and web Exam Results screens, so the two cannot drift.
 */

export interface PickableExamType {
  id: string;
  name: string;
  isActive: boolean;
}

type StudentLike = { enrollmentId?: string; academicYear?: number; rawSnapshot?: any } | null | undefined;

const enrollmentsOf = (student: StudentLike): any[] =>
  student?.rawSnapshot?.Enrollments ?? student?.rawSnapshot?.enrollments ?? [];

/** "2025-2026" for an academic year stored by its END year (2026). */
export const yearLabel = (endYear: number): string => `${endYear - 1}-${endYear}`;

/**
 * The enrolment for an academic year chosen as "2025-2026".
 *
 * Enrollment.AcademicYear stores the END year of the range (2026, not 2025), so
 * the match rebuilds the same "start-end" pair - the way the web report card
 * does - rather than comparing against the parsed start year.
 *
 * Only the CURRENT year falls back to the student's current enrolment (the snapshot may
 * not list it). Any other year with no enrolment on record has none: the report card is
 * looked up by enrolment alone, so falling back there showed this year's marks under
 * every year picked (QA: "report card data remains unchanged" when switching years).
 */
export const enrollmentIdForYear = (student: StudentLike, selectedYear: string): string | undefined => {
  if (!student) return undefined;
  const match = enrollmentsOf(student).find((e: any) => {
    const endYear = e.AcademicYear ?? e.academicYear;
    return yearLabel(endYear) === selectedYear;
  });
  const matched = match?.Id ?? match?.id;
  if (matched) return matched;
  const isCurrentYear = !!student.academicYear && yearLabel(student.academicYear) === selectedYear;
  return isCurrentYear ? student.enrollmentId : undefined;
};

/**
 * The years a family can pick for exam results: every year the child was enrolled, plus the
 * current one, newest first. No invented years - the screens used to offer the current year
 * and the three before it (the web also the next one, selected by default) whether or not
 * the child was at the school then.
 */
export const academicYearOptions = (student: StudentLike): string[] => {
  const endYears = new Set<number>();
  for (const e of enrollmentsOf(student)) {
    const endYear = Number(e.AcademicYear ?? e.academicYear);
    if (endYear > 2000) endYears.add(endYear);
  }
  if (student?.academicYear && student.academicYear > 2000) endYears.add(student.academicYear);
  return [...endYears].sort((a, b) => b - a).map(yearLabel);
};

/**
 * Which exam stays selected once the list of published exam types arrives:
 * the current one if it is still on offer, otherwise the first, otherwise none -
 * never an exam whose results the family cannot see.
 */
export const examToSelect = (types: PickableExamType[], current: string): string => {
  const offered = types.filter(t => t.isActive);
  if (offered.some(t => t.id === current)) return current;
  return offered[0]?.id ?? '';
};
